import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from 'recharts';
interface BarChartVisualProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  barColor?: string;
  hideValues?: boolean;
  valueDisplay?: 'tooltip' | 'on-bar' | 'on-axis-line';
  mode?: 'stimulus' | 'support';
}
export function BarChartVisual({
  data,
  xAxisLabel = '',
  yAxisLabel = '',
  barColor = '#6366f1',
  hideValues = true,
  valueDisplay,
  mode = 'stimulus'
}: BarChartVisualProps) {
  // In support mode, we might want to force showing values to help explain the answer
  const effectiveHideValues = mode === 'support' ? false : hideValues;
  const effectiveValueDisplay = valueDisplay || (effectiveHideValues ? 'on-axis-line' : 'on-bar');
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-11336">
        <span className="font-semibold text-slate-700" data-id="element-11337">{item.label}</span>
        {!effectiveHideValues && <span className="text-slate-500 ml-2" data-id="element-11338">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-11339">
      {/* Visually hidden table for screen readers */}
      <table className="sr-only" data-id="element-11340">
        <caption data-id="element-11341">
          Staafdiagram data
          {xAxisLabel || yAxisLabel ? ` voor ${xAxisLabel} en ${yAxisLabel}` : ''}
        </caption>
        <thead data-id="element-11342">
          <tr data-id="element-11343">
            <th scope="col" data-id="element-11344">{xAxisLabel || 'Categorie'}</th>
            <th scope="col" data-id="element-11345">{yAxisLabel || 'Waarde'}</th>
          </tr>
        </thead>
        <tbody data-id="element-11346">
          {data.map((item, i) => <tr key={i} data-id="element-11347">
              <td data-id="element-11348">{item.label}</td>
              <td data-id="element-11349">{item.value}</td>
            </tr>)}
        </tbody>
      </table>

      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" data-id="element-11350">
        <ResponsiveContainer width="100%" height="100%" data-id="element-11351">
          <BarChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-11352">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-11353" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-11354" />

            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-11355" />

            <Tooltip content={<CustomTooltip data-id="element-11357" />} cursor={{
            fill: '#f1f5f9'
          }} data-id="element-11356" />

            {effectiveValueDisplay === 'on-axis-line' && Array.from(new Set(data.map(d => d.value))).map((val, i) => <ReferenceLine key={`ref-${i}`} y={val} stroke="#94a3b8" strokeDasharray="3 3" opacity={0.5} data-id="element-11358" />)}

            <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-11359">
              {effectiveValueDisplay === 'on-bar' && <LabelList dataKey="value" position="top" fill="#475569" fontSize={12} fontWeight={600} data-id="element-11360" />}
              {data.map((_entry, index) => <Cell key={`cell-${index}`} fill={barColor} data-id="element-11361" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
}